import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    LayoutDashboard,
    Users,
    Home,
    MessageSquare,
    Shield,
    TrendingUp,
    Award,
    Euro,
    Calendar,
    Star,
    Loader2,
    Search,
    Trash2,
    Ban,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    MapPin,
    Mail,
    Eye,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== STAT CARD ====================
const StatCard = ({ icon, label, value, sub, color = 'text-scalimmo-navy' }) => (
    <div className="bg-white rounded-xl border border-border/60 p-5 shadow-soft">
        <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-muted/50 ${color}`}>{icon}</div>
            <span className="text-sm text-muted-foreground">{label}</span>
        </div>
        <p className="text-3xl font-heading font-semibold text-scalimmo-navy">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
);

// ==================== STATS TAB ====================
const StatsTab = ({ stats }) => {
    if (!stats) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
    return (
        <div className="space-y-6" data-testid="admin-stats">
            <h2 className="font-heading text-xl font-semibold text-scalimmo-navy">Vue d'ensemble</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={<Users className="w-5 h-5" />} label="Agents" value={stats.users.total_agents} sub={`${stats.users.top_performers} Top Performers`} />
                <StatCard icon={<Users className="w-5 h-5" />} label="Vendeurs" value={stats.users.total_sellers} />
                <StatCard icon={<Home className="w-5 h-5" />} label="Biens publiés" value={stats.properties.total} sub={`${stats.properties.available} disponibles`} />
                <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Nouveaux (7j)" value={stats.users.new_users_week} color="text-green-600" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={<Calendar className="w-5 h-5" />} label="Rendez-vous" value={stats.activity.total_appointments} />
                <StatCard icon={<MessageSquare className="w-5 h-5" />} label="Conversations" value={stats.activity.total_conversations} sub={`${stats.activity.total_messages} messages`} />
                <StatCard icon={<Star className="w-5 h-5" />} label="Avis" value={stats.activity.total_reviews} />
                <StatCard icon={<Euro className="w-5 h-5" />} label="MRR" value={`${stats.subscriptions.mrr}€`} sub={`${stats.subscriptions.pro} Pro, ${stats.subscriptions.elite} Elite`} color="text-green-600" />
            </div>
        </div>
    );
};

// ==================== USERS TAB ====================
const UsersTab = ({ token }) => {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (typeFilter !== 'all') params.append('user_type', typeFilter);
            if (searchQuery) params.append('search', searchQuery);
            const res = await axios.get(`${API}/admin/users?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data.users);
            setTotal(res.data.total);
            setPages(res.data.pages);
        } catch (e) { toast.error('Erreur chargement'); }
        finally { setLoading(false); }
    }, [token, page, typeFilter, searchQuery]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const toggleSuspend = async (user) => {
        try {
            await axios.put(`${API}/admin/users/${user.id}`, { is_suspended: !user.is_suspended }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(user.is_suspended ? 'Compte réactivé' : 'Compte suspendu');
            fetchUsers();
        } catch (e) { toast.error('Erreur'); }
    };

    const toggleTopPerformer = async (user) => {
        try {
            await axios.put(`${API}/admin/users/${user.id}`, { is_top_performer: !user.is_top_performer }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(user.is_top_performer ? 'Top Performer retiré' : 'Promu Top Performer');
            fetchUsers();
        } catch (e) { toast.error('Erreur'); }
    };

    const deleteUser = async (userId) => {
        try {
            await axios.delete(`${API}/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Utilisateur supprimé');
            setConfirmDelete(null);
            fetchUsers();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const roleBadge = (type) => {
        const map = { agent: 'bg-blue-100 text-blue-700', seller: 'bg-green-100 text-green-700', admin: 'bg-red-100 text-red-700' };
        const labels = { agent: 'Agent', seller: 'Vendeur', admin: 'Admin' };
        return <Badge className={map[type] || 'bg-gray-100'}>{labels[type] || type}</Badge>;
    };

    return (
        <div className="space-y-4" data-testid="admin-users">
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2 flex-1">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Rechercher par nom, email, ville..." value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                        data-testid="admin-search-users"
                    />
                </div>
                <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="agent">Agents</SelectItem>
                        <SelectItem value="seller">Vendeurs</SelectItem>
                        <SelectItem value="admin">Admins</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground self-center">{total} résultats</span>
            </div>

            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
                <div className="bg-white rounded-xl border border-border/60 shadow-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/30 border-b border-border/40">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Utilisateur</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Rôle</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Ville</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Statut</th>
                                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id} className="border-b border-border/20 hover:bg-muted/10" data-testid={`admin-user-${user.id}`}>
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-scalimmo-navy">{user.first_name} {user.last_name}</p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{user.email}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {roleBadge(user.user_type)}
                                            {user.is_top_performer && <Badge className="bg-yellow-100 text-yellow-700"><Award className="w-3 h-3 mr-1" />Top</Badge>}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {user.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{user.city}</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        {user.is_suspended
                                            ? <Badge className="bg-red-100 text-red-700">Suspendu</Badge>
                                            : <Badge className="bg-green-100 text-green-700">Actif</Badge>
                                        }
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            {user.user_type === 'agent' && (
                                                <Button variant="ghost" size="sm" onClick={() => toggleTopPerformer(user)}
                                                    className={user.is_top_performer ? 'text-yellow-600' : 'text-muted-foreground'}
                                                    title={user.is_top_performer ? 'Retirer Top Performer' : 'Promouvoir Top Performer'}
                                                >
                                                    <Award className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {user.user_type !== 'admin' && (
                                                <>
                                                    <Button variant="ghost" size="sm" onClick={() => toggleSuspend(user)}
                                                        className={user.is_suspended ? 'text-green-600' : 'text-orange-600'}
                                                        title={user.is_suspended ? 'Réactiver' : 'Suspendre'}
                                                    >
                                                        {user.is_suspended ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirmDelete(user)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
                        <p className="text-sm text-muted-foreground">Page {page} / {pages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmer la suppression</DialogTitle>
                        <DialogDescription>
                            Supprimer <strong>{confirmDelete?.first_name} {confirmDelete?.last_name}</strong> ({confirmDelete?.email}) ?
                            Cette action supprimera aussi ses biens, disponibilités et conversations.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={() => deleteUser(confirmDelete?.id)}>Supprimer</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// ==================== PROPERTIES TAB ====================
const PropertiesTab = ({ token }) => {
    const [properties, setProperties] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const fetchProperties = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (searchQuery) params.append('search', searchQuery);
            const res = await axios.get(`${API}/admin/properties?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProperties(res.data.properties);
            setTotal(res.data.total);
            setPages(res.data.pages);
        } catch (e) { toast.error('Erreur'); }
        finally { setLoading(false); }
    }, [token, page, statusFilter, searchQuery]);

    useEffect(() => { fetchProperties(); }, [fetchProperties]);

    const updateStatus = async (id, status) => {
        try {
            await axios.put(`${API}/admin/properties/${id}`, { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Statut mis à jour: ${status}`);
            fetchProperties();
        } catch (e) { toast.error('Erreur'); }
    };

    const deleteProperty = async (id) => {
        try {
            await axios.delete(`${API}/admin/properties/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Bien supprimé');
            fetchProperties();
        } catch (e) { toast.error('Erreur'); }
    };

    const statusBadge = (s) => {
        const map = { available: 'bg-green-100 text-green-700', sold: 'bg-gray-100 text-gray-700', pending: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700' };
        const labels = { available: 'Disponible', sold: 'Vendu', pending: 'En attente', rejected: 'Rejeté' };
        return <Badge className={map[s] || 'bg-gray-100'}>{labels[s] || s}</Badge>;
    };

    return (
        <div className="space-y-4" data-testid="admin-properties">
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2 flex-1">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Rechercher par titre, ville..." value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setPage(1); }} />
                </div>
                <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="available">Disponible</SelectItem>
                        <SelectItem value="sold">Vendu</SelectItem>
                        <SelectItem value="pending">En attente</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground self-center">{total} biens</span>
            </div>

            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
                <div className="bg-white rounded-xl border border-border/60 shadow-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/30 border-b border-border/40">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Bien</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Vendeur</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Prix</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Statut</th>
                                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {properties.map(prop => (
                                <tr key={prop.id} className="border-b border-border/20 hover:bg-muted/10">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-scalimmo-navy">{prop.title}</p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{prop.city}</p>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {prop.seller ? `${prop.seller.first_name} ${prop.seller.last_name}` : '-'}
                                        <p className="text-xs">{prop.seller?.email}</p>
                                    </td>
                                    <td className="px-4 py-3 font-medium">{prop.price?.toLocaleString('fr-FR')} €</td>
                                    <td className="px-4 py-3">{statusBadge(prop.status)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            {prop.status !== 'available' && (
                                                <Button variant="ghost" size="sm" className="text-green-600" onClick={() => updateStatus(prop.id, 'available')} title="Approuver">
                                                    <CheckCircle className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {prop.status === 'available' && (
                                                <Button variant="ghost" size="sm" className="text-orange-600" onClick={() => updateStatus(prop.id, 'pending')} title="Mettre en attente">
                                                    <Ban className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteProperty(prop.id)}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
                        <p className="text-sm text-muted-foreground">Page {page} / {pages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== CONVERSATIONS TAB ====================
const ConversationsTab = ({ token }) => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);

    useEffect(() => { fetchConversations(); }, [page]);

    const fetchConversations = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/conversations?page=${page}&limit=15`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConversations(res.data.conversations);
            setPages(res.data.pages);
        } catch (e) { toast.error('Erreur'); }
        finally { setLoading(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-conversations">
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : conversations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">Aucune conversation</div>
            ) : (
                <div className="bg-white rounded-xl border border-border/60 shadow-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/30 border-b border-border/40">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Participants</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Bien associé</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Messages</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Dernier message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {conversations.map(conv => (
                                <tr key={conv.id} className="border-b border-border/20 hover:bg-muted/10">
                                    <td className="px-4 py-3">
                                        {conv.participants_info?.map((p, i) => (
                                            <div key={i} className="flex items-center gap-1 text-xs">
                                                <Badge className={p?.user_type === 'agent' ? 'bg-blue-100 text-blue-700 text-[10px]' : 'bg-green-100 text-green-700 text-[10px]'}>
                                                    {p?.user_type === 'agent' ? 'A' : 'V'}
                                                </Badge>
                                                <span>{p?.first_name} {p?.last_name}</span>
                                                <span className="text-muted-foreground">({p?.email})</span>
                                            </div>
                                        ))}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">
                                        {conv.property ? `${conv.property.title} - ${conv.property.city}` : '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge variant="secondary">{conv.message_count}</Badge>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px] truncate">
                                        {conv.last_message}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
                        <p className="text-sm text-muted-foreground">Page {page} / {pages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== ADMIN PAGE ====================
const AdminPage = () => {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (!user || user.user_type !== 'admin') {
            navigate('/');
            return;
        }
        fetchStats();
    }, [user, navigate]);

    const fetchStats = async () => {
        try {
            const res = await axios.get(`${API}/admin/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
        } catch (e) { console.error(e); }
    };

    if (!user || user.user_type !== 'admin') return null;

    return (
        <div className="min-h-screen pt-24 pb-16 bg-muted/10" data-testid="admin-page">
            <div className="container-scalimmo">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-lg bg-scalimmo-navy flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-semibold text-scalimmo-navy">Administration</h1>
                        <p className="text-sm text-muted-foreground">Gérez votre plateforme Scalimmo</p>
                    </div>
                </div>

                <Tabs defaultValue="overview">
                    <TabsList className="mb-6">
                        <TabsTrigger value="overview" className="gap-2"><LayoutDashboard className="w-4 h-4" />Vue d'ensemble</TabsTrigger>
                        <TabsTrigger value="users" className="gap-2"><Users className="w-4 h-4" />Utilisateurs</TabsTrigger>
                        <TabsTrigger value="properties" className="gap-2"><Home className="w-4 h-4" />Biens</TabsTrigger>
                        <TabsTrigger value="conversations" className="gap-2"><MessageSquare className="w-4 h-4" />Messages</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview"><StatsTab stats={stats} /></TabsContent>
                    <TabsContent value="users"><UsersTab token={token} /></TabsContent>
                    <TabsContent value="properties"><PropertiesTab token={token} /></TabsContent>
                    <TabsContent value="conversations"><ConversationsTab token={token} /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default AdminPage;
